import { useEffect, useMemo, useState } from 'react';
import { DropzoneOptions, useDropzone } from 'react-dropzone';
import { CircularProgress } from '@mui/material';
import { UPLOAD_PRESET, uploadImage, UploadImageResponse } from '@modules/image-upload';

export interface PictureUploadProps {
    presetType: UPLOAD_PRESET;
    fileDisplayName?: string;
    uploadTags?: string[];
    htmlId?: string;
    onProgressUpdate?: (progress: number) => void;
    onUploadFinish?: (fileInfo: UploadImageResponse) => void;
}

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: 'max(20px, min(5vw, 5vh))',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};

const activeStyle = { borderColor: '#2196f3' };

const acceptStyle = { borderColor: '#00e676' };

const rejectStyle = { borderColor: '#ff1744' };

const dropZoneOptions: DropzoneOptions = {
    accept: { 'image/*': ['.png', '.gif', '.jpeg', '.jpg'] },
};

export default function PictureUploadForm({ htmlId, presetType, ...props }: PictureUploadProps) {
    const [uploadProgress, setUploadProgress] = useState<null | number>(null);

    const { acceptedFiles, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
        useDropzone(dropZoneOptions);

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isDragActive ? activeStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isDragActive, isDragReject, isDragAccept],
    );

    useEffect(() => {
        let mounted = true;
        const processUpload = async () => {
            if (acceptedFiles[0]) {
                // Handle uploading the image if 'onUploadFinish' prop is passed
                const file = await uploadImage(
                    acceptedFiles[0],
                    presetType,
                    props.uploadTags || [],
                );

                props.onUploadFinish?.(file);
                setUploadProgress(null);
            }
        };
        if (mounted) {
            processUpload();
        }
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-compiler/react-compiler
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acceptedFiles]);

    const isUploading = uploadProgress && uploadProgress > 0;

    return (
        <div {...getRootProps({ style })}>
            {/* matching the HTML id allows uploading image by clicking input label */}
            <input id={htmlId} {...getInputProps()} />
            {isUploading ? <CircularProgress variant="determinate" value={uploadProgress} /> : null}

            {isUploading ? (
                <p>Uploading...</p>
            ) : (
                <p>Drag 'n' drop an image here, or click to select an image</p>
            )}
        </div>
    );
}
